/// <amd-module name="Wit/Common/Controls/Boards/WitTeachingBubble" />

import * as React from "react";
import { useService } from "VSS/Platform/Layout";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { ITeachingBubbleProps, TeachingBubble } from "VSSUI/TeachingBubble";
import { IOrigin, Location } from "VSSUI/Utilities/Position";

interface IWitTeachingBubbleProps {
    /**
     * The class name of the element to anchor the teaching bubble to.
     */
    anchorElementClassName: string;

    /**
     * The settings entry key to use to determine whether or not to show the teaching bubble. (Unique per bubble)
     * If the setting is not found, the teaching bubble will be shown.
     */
    settingsEntryKey: string;

    /**
     * The settings key override to not use default bubble settings space. (Optional)
     * Resulting settings key will be: {settingKeyOverride}/{settingsEntryKey}
     */
    settingKeyOverride?: string;

    /**
     * Whether or not the teaching bubble can be shown.
     */
    canShow: boolean;

    /**
     * The anchor origin of the teaching bubble.
     */
    anchorOrigin?: IOrigin;

    /**
     * Callback when the teaching bubble is dismissed. Teaching bubble will be hidden even if this is not provided.
     */
    onDismiss?: () => void;
}

export type WitTeachingBubbleProps = IWitTeachingBubbleProps & Omit<ITeachingBubbleProps, "anchorOrigin" | "onDismiss" | "anchorElement">;

const witTeachingBubblesSettings = "WITTeachingBubbles/Settings";
const defaultAnchorOrigin: IOrigin = {
    horizontal: Location.end,
    vertical: Location.end
};

// caching statically in case multiple bubbles are shown at the same time
let bubbleSettings: Promise<{ [key: string]: any }> | undefined = undefined;

export const WitTeachingBubble = (props: WitTeachingBubbleProps): JSX.Element | null => {
    const { anchorElementClassName, settingsEntryKey, settingKeyOverride, canShow, anchorOrigin, onDismiss } = props;
    const settingsService = useService<ISettingsService>("ISettingsService");
    const [isDismissed, setIsDismissed] = React.useState(false);
    const [hasAlreadyBeenSeen, setHasAlreadyBeenSeen] = React.useState<boolean | undefined>(undefined);

    React.useEffect(() => {
        const getSettingEntries = async () => {
            let settings: { [key: string]: any } | undefined;
            if (settingKeyOverride) {
                settings = await settingsService.getEntriesAsync(settingKeyOverride, SettingsUserScope.Me);
            } else {
                if (!bubbleSettings) {
                    bubbleSettings = settingsService.getEntriesAsync(witTeachingBubblesSettings, SettingsUserScope.Me);
                }
                settings = await bubbleSettings;
            }
            setHasAlreadyBeenSeen(!!settings[settingsEntryKey]);
        };
        getSettingEntries().catch(err => {
            console.error(err);
            bubbleSettings = undefined;
            setHasAlreadyBeenSeen(undefined);
        });
    }, [settingsService, settingsEntryKey, settingKeyOverride, setHasAlreadyBeenSeen]);

    const onDismissInternal = () => {
        setIsDismissed(true);
        const settings = settingKeyOverride ?? witTeachingBubblesSettings;
        settingsService.setEntries(
            {
                [`${settings}/${settingsEntryKey}`]: true
            },
            SettingsUserScope.Me
        );
        onDismiss?.();
    };

    const shouldShow = hasAlreadyBeenSeen === false && !isDismissed && canShow;
    if (!shouldShow) {
        return null;
    }

    const anchorElement = document.getElementsByClassName(anchorElementClassName)[0] as HTMLElement;
    if (!anchorElement) {
        return null;
    }

    return (
        <TeachingBubble anchorElement={anchorElement} anchorOrigin={anchorOrigin ?? defaultAnchorOrigin} onDismiss={onDismissInternal} {...props} />
    );
};
